/**
 * pendingActionsService.js
 *
 * File d'attente simple pour les actions de pointage sans réseau.
 * Stockage localStorage — survit à la fermeture du navigateur.
 *
 * Règles :
 * - Max 4 actions par journée (START_WORK, START_BREAK, END_BREAK, END_WORK)
 * - Sync toujours dans l'ordre FIFO
 * - Vérification anti-doublon côté Firebase avant chaque écriture
 */

import { db } from '../firebase-config';
import {
  doc,
  getDoc,
  setDoc,
  addDoc,
  updateDoc,
  getDocs,
  collection,
  query,
  where,
  Timestamp
} from 'firebase/firestore';

const STORAGE_KEY = 'horae_pending_actions';

// ─── Lecture / écriture localStorage ────────────────────────────────────────

export function getQueue() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
}

function saveQueue(queue) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
}

// ─── Ajouter une action à la queue ──────────────────────────────────────────

export function enqueue(action) {
  const queue = getQueue();
  queue.push(action);
  saveQueue(queue);
}

// ─── Nombre d'actions en attente ────────────────────────────────────────────

export function getPendingCount() {
  return getQueue().length;
}

// ─── Vider la queue (après sync réussie) ────────────────────────────────────

function clearQueue() {
  localStorage.removeItem(STORAGE_KEY);
}

// ─── Traitement d'une action vers Firebase ──────────────────────────────────

async function processAction(action) {
  switch (action.type) {

    case 'START_WORK': {
      // Anti-doublon : le doc existe déjà ?
      const existing = await getDoc(doc(db, 'workSessions', action.sessionId));
      if (existing.exists()) return; // déjà là, on skip

      await setDoc(doc(db, 'workSessions', action.sessionId), {
        employeeId:     action.employeeId,
        employeeEmail:  action.employeeEmail,
        employeeName:   action.employeeName,
        siteDeTravail:  action.siteDeTravail,
        startTime:      Timestamp.fromMillis(action.timestamp),
        startLocation:  action.location,
        status:         'active',
        isOnBreak:      false,
        totalPauseTime: 0,
        totalWorkTime:  0,
        device:         action.device,
        createdAt:      Timestamp.fromMillis(action.timestamp)
      });
      break;
    }

    case 'START_BREAK': {
      // Anti-doublon : une pause active existe déjà pour cette session ?
      const existingBreak = await getDocs(query(
        collection(db, 'breaks'),
        where('sessionId', '==', action.sessionId),
        where('status', '==', 'active')
      ));
      if (!existingBreak.empty) return; // déjà là, on skip

      // Créer la pause avec l'ID prédéfini
      await setDoc(doc(db, 'breaks', action.breakId), {
        sessionId:     action.sessionId,
        startTime:     Timestamp.fromMillis(action.timestamp),
        startLocation: action.location,
        status:        'active',
        createdAt:     Timestamp.fromMillis(action.timestamp)
      });

      // Mettre à jour la session
      await updateDoc(doc(db, 'workSessions', action.sessionId), {
        isOnBreak:   true,
        activeBreak: {
          id:        action.breakId,
          startTime: Timestamp.fromMillis(action.timestamp)
        }
      });
      break;
    }

    case 'END_BREAK': {
      // Anti-doublon : la pause est déjà terminée ?
      const breakDoc = await getDoc(doc(db, 'breaks', action.breakId));
      if (!breakDoc.exists()) return;
      if (breakDoc.data().status === 'completed') return;

      const breakStartTime = breakDoc.data().startTime.toMillis();
      const duration = action.timestamp - breakStartTime;

      await updateDoc(doc(db, 'breaks', action.breakId), {
        endTime:     Timestamp.fromMillis(action.timestamp),
        endLocation: action.location,
        status:      'completed',
        duration:    duration
      });

      // Mettre à jour totalPauseTime sur la session
      const sessionDoc = await getDoc(doc(db, 'workSessions', action.sessionId));
      if (sessionDoc.exists()) {
        const currentPause = sessionDoc.data().totalPauseTime || 0;
        await updateDoc(doc(db, 'workSessions', action.sessionId), {
          isOnBreak:      false,
          activeBreak:    null,
          totalPauseTime: currentPause + duration
        });
      }
      break;
    }

    case 'END_WORK': {
      // Anti-doublon : session déjà terminée ?
      const sessionDoc = await getDoc(doc(db, 'workSessions', action.sessionId));
      if (!sessionDoc.exists()) return;
      if (sessionDoc.data().status === 'completed') return;

      const sessionData  = sessionDoc.data();
      const startTime    = sessionData.startTime.toMillis();
      const endTime      = action.timestamp;
      const totalPause   = sessionData.totalPauseTime || 0;
      const totalSession = endTime - startTime;
      const totalWork    = totalSession - totalPause;

      await updateDoc(doc(db, 'workSessions', action.sessionId), {
        endTime:          Timestamp.fromMillis(endTime),
        endLocation:      action.location,
        status:           'completed',
        totalSessionTime: totalSession,
        totalPauseTime:   totalPause,
        totalWorkTime:    totalWork
      });
      break;
    }

    default:
      console.warn('[PendingActions] Type inconnu:', action.type);
  }
}

// ─── Flush : envoyer toutes les actions en attente dans l'ordre ──────────────

export async function flushQueue() {
  const queue = getQueue();
  if (queue.length === 0) return { flushed: 0, remaining: 0 };

  const remaining = [...queue];
  let flushed = 0;

  for (const action of queue) {
    try {
      await processAction(action);
      remaining.shift(); // supprime le premier élément (FIFO)
      flushed++;
    } catch (error) {
      console.error('[PendingActions] Échec sync action:', action.type, error);
      break; // stop : on ne saute pas une action, on attend le prochain essai
    }
  }

  saveQueue(remaining);
  return { flushed, remaining: remaining.length };
}
